'use client';

import { useState } from 'react';
import { Sidebar } from 'primereact/sidebar';
import { submitFeedback } from '@/lib/support/supportService';

const LABELS = ['', 'Poor', 'Fair', 'Good', 'Great', 'Excellent'];

export const FeedbackSidebar = ({ ticket, onHide, onSuccess, toastRef }) => {
    const [rating,  setRating]  = useState(0);
    const [hover,   setHover]   = useState(0);
    const [comment, setComment] = useState('');
    const [busy,    setBusy]    = useState(false);
    const [done,    setDone]    = useState(false);

    const handleSubmit = async () => {
        if (!rating) {
            toastRef.current?.show({ severity: 'warn', summary: 'Rating required', detail: 'Please select a star rating.', life: 3000 });
            return;
        }
        setBusy(true);
        try {
            const res = await submitFeedback(ticket.ticketNumber || ticket._id, rating, comment.trim());
            if (res.success) {
                setDone(true);
                onSuccess?.({ rating, comment: comment.trim() });
            } else {
                toastRef.current?.show({ severity: 'error', summary: 'Error', detail: res.message || 'Could not submit feedback.', life: 3500 });
            }
        } catch {
            toastRef.current?.show({ severity: 'error', summary: 'Error', detail: 'Something went wrong.', life: 3500 });
        } finally {
            setBusy(false);
        }
    };

    const header = (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
                width: 34, height: 34, borderRadius: 10,
                background: 'rgba(245,158,11,0.15)',
                border: '1.5px solid rgba(245,158,11,0.35)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
                <i className="pi pi-star-fill" style={{ fontSize: '0.9rem', color: '#f59e0b' }} />
            </div>
            <div>
                <p style={{ margin: 0, fontSize: '0.95rem', fontWeight: 800, color: '#fff' }}>Leave Feedback</p>
                <p style={{ margin: 0, fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)' }}>
                    #{ticket?.ticketNumber || ticket?._id}
                </p>
            </div>
        </div>
    );

    return (
        <Sidebar
            visible={!!ticket}
            onHide={onHide}
            position="right"
            style={{ width: '100%', maxWidth: 420, background: '#ffffff' }}
            header={header}
            pt={{
                header:      { style: { background: 'var(--primary-color)', padding: '13px 18px' } },
                closeButton: { style: { color: 'rgba(255,255,255,0.6)' } },
            }}
        >
            {done ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', paddingTop: 60, gap: 16, textAlign: 'center' }}>
                    <div style={{
                        width: 72, height: 72, borderRadius: '50%',
                        background: 'rgba(245,158,11,0.12)',
                        border: '2px solid rgba(245,158,11,0.35)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        boxShadow: '0 0 28px rgba(245,158,11,0.2)',
                    }}>
                        <i className="pi pi-check" style={{ fontSize: '1.8rem', color: '#f59e0b' }} />
                    </div>
                    <div>
                        <p style={{ margin: '0 0 6px', fontSize: '1.1rem', fontWeight: 800, color: '#1e293b' }}>Thank you!</p>
                        <p style={{ margin: 0, fontSize: '0.84rem', color: '#64748b', lineHeight: 1.6 }}>
                            Your feedback helps us improve our support experience.
                        </p>
                    </div>
                    <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                        {[1,2,3,4,5].map(s => (
                            <span key={s} style={{ fontSize: '1.5rem', color: s <= rating ? '#f59e0b' : '#e2e8f0' }}>★</span>
                        ))}
                    </div>
                    <button
                        onClick={onHide}
                        style={{
                            marginTop: 12, padding: '10px 28px', borderRadius: 10,
                            background: 'linear-gradient(135deg,#f59e0b,#d97706)',
                            border: 'none', color: '#000', fontWeight: 700, fontSize: '0.88rem',
                            cursor: 'pointer',
                        }}
                    >
                        Done
                    </button>
                </div>
            ) : (
                <div style={{ padding: '8px 0' }}>
                    {/* Ticket subject */}
                    <div style={{
                        padding: '12px 14px', borderRadius: 12, marginBottom: 24,
                        background: '#f8fafc',
                        border: '1px solid #e2e8f0',
                    }}>
                        <p style={{ margin: '0 0 2px', fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>
                            {ticket?.queryType || 'Support Request'}
                        </p>
                        <p style={{
                            margin: 0, fontSize: '0.75rem', color: '#64748b',
                            display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        }}>
                            {ticket?.description || ticket?.message || ''}
                        </p>
                    </div>

                    {/* Star rating */}
                    <p style={{ margin: '0 0 12px', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        How was your experience?
                    </p>
                    <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                        {[1,2,3,4,5].map(s => (
                            <span
                                key={s}
                                onClick={() => setRating(s)}
                                onMouseEnter={() => setHover(s)}
                                onMouseLeave={() => setHover(0)}
                                style={{
                                    fontSize: '2.4rem', cursor: 'pointer',
                                    transition: 'transform 0.12s, color 0.12s',
                                    color: s <= (hover || rating) ? '#f59e0b' : '#e2e8f0',
                                    filter: s <= (hover || rating) ? 'drop-shadow(0 0 8px rgba(245,158,11,0.55))' : 'none',
                                    transform: s === (hover || rating) ? 'scale(1.18)' : 'scale(1)',
                                    display: 'inline-block',
                                }}
                            >
                                ★
                            </span>
                        ))}
                    </div>
                    {(hover || rating) > 0 && (
                        <p style={{ margin: '0 0 20px', fontSize: '0.78rem', fontWeight: 700, color: '#f59e0b' }}>
                            {LABELS[hover || rating]}
                        </p>
                    )}
                    {!(hover || rating) && <div style={{ marginBottom: 20 }} />}

                    {/* Comment (optional) */}
                    <p style={{ margin: '0 0 8px', fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
                        Additional comments <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0 }}>(optional)</span>
                    </p>
                    <textarea
                        value={comment}
                        onChange={e => setComment(e.target.value)}
                        placeholder="Tell us what we did well or where we can improve…"
                        rows={4}
                        disabled={busy}
                        maxLength={1000}
                        style={{
                            width: '100%', boxSizing: 'border-box',
                            background: '#f8fafc',
                            border: '1px solid #e2e8f0',
                            borderRadius: 12, color: '#1e293b',
                            padding: '11px 14px', fontSize: '0.87rem',
                            resize: 'none', outline: 'none', fontFamily: 'inherit',
                            lineHeight: 1.6, marginBottom: 6,
                            transition: 'border-color 0.15s',
                        }}
                        onFocus={e => { e.target.style.borderColor = 'rgba(245,158,11,0.45)'; }}
                        onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
                    />
                    <p style={{ margin: '0 0 18px', fontSize: '0.68rem', color: '#94a3b8', textAlign: 'right' }}>
                        {comment.length}/1000
                    </p>

                    {/* Submit */}
                    <button
                        onClick={handleSubmit}
                        disabled={busy || !rating}
                        style={{
                            width: '100%', padding: '13px', borderRadius: 12,
                            background: rating ? 'linear-gradient(135deg,#f59e0b,#d97706)' : 'rgba(245,158,11,0.15)',
                            border: 'none',
                            color: rating ? '#000' : 'rgba(245,158,11,0.5)',
                            fontSize: '0.92rem', fontWeight: 800,
                            cursor: (busy || !rating) ? 'not-allowed' : 'pointer',
                            transition: 'all 0.15s',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                        }}
                    >
                        <i className={`pi ${busy ? 'pi-spin pi-spinner' : 'pi-check'}`} style={{ fontSize: '0.85rem' }} />
                        {busy ? 'Submitting…' : 'Submit Feedback'}
                    </button>
                </div>
            )}
        </Sidebar>
    );
};
